import React, { FC, useState, useEffect } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Stack from "../stack/Stack";
import { useBackgroundColor } from "../../utils";
import Typography from "../typography/Typography";
import { ChevronDown } from "../icon";
import { useDataLayerPush } from "../../hooks/useDataLayerPush";
import { isBrowser } from "@zempler/utils";

// Interface

export interface AccordionProps {
  items: AccordionItem[];
}

interface AccordionItem {
  title: string;
  components?: React.ReactNode;
  content?: React.ReactNode;
}

interface StyledAccordionProps {
  isOpen: boolean;
}

// Styles

export const StyledAccordion = styled(Stack)`
  ${({ theme: { space } }) => css`
  `};
`;

const StyledBody = styled.div<StyledAccordionProps>`
  ${({ theme: { space, borderRadii, colors }, isOpen }) => css`
    ${useBackgroundColor("white")}
    transition: all 0.2s ease-in-out;
    display: grid;
    transition: all 0.5s ease-in-out;
    grid-template-rows: ${isOpen ? "1fr" : "0fr"};

    > div {
      transition: all 0.5s ease-in-out;
      overflow: hidden;
      padding-top: ${isOpen ? space.fluidSmallToLarge : "0"};
      padding-bottom: ${isOpen ? space.fluidSmallToLarge : "0"};
      padding-left: ${space.fluidSmallToLarge};
      padding-right: ${space.fluidSmallToLarge};

      ${isOpen &&
      css`
        border-bottom: solid 1px currentColor;
        border-left: solid 1px currentColor;
        border-right: solid 1px currentColor;
        border-radius: 0 0 ${borderRadii.small} ${borderRadii.small};
      `}
    }
  `};
`;

const StyledHeader = styled.button<StyledAccordionProps>`
  ${({ theme: { space, borderRadii, colors }, isOpen }) => css`
    ${useBackgroundColor("neutral")}
    padding: ${space.fluidXSmallToSmall} ${space.fluidSmallToMedium};
    border: solid 1px currentColor;
    border-radius: ${borderRadii.small};
    display: flex;
    justify-content: space-between;
    gap: ${space.small};
    width: 100%;
    text-align: left;
    transition: all 0.2s ease-in-out;

    svg {
      transition: transform 0.3s;
      transform: rotate(${isOpen ? "180deg" : "0deg"});
    }

    &:hover {
      box-shadow: 0 0 0 1px ${colors.darkBlue};

      ${isOpen &&
      css`
        ~ * > div {
          box-shadow: 0 0 0 1px ${colors.darkBlue};
        }
      `}
    }

    ${isOpen &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}
  `};
`;

// AccordionItem Component
const AccordionItem: FC<AccordionItem> = ({ title, content, components }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { dataLayerPush } = useDataLayerPush();

  const toggleAccordion = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);

    dataLayerPush({
      event: "click accordion",
      clickText: title,
      clickAccordionAction: isOpen ? "closed" : "opened",
    });
  };

  const generateId = (str: string) => str.toLowerCase().replace(/\s+/g, "-");

  const handleHashChange = () => {
    if (isBrowser) {
      if (window.location.hash === `#${generateId(title)}`) {
        setIsOpen(true);
        document.getElementById(generateId(title))?.scrollIntoView();
      }
    }
  };

  useEffect(() => {
    if (isBrowser) {
      handleHashChange(); // Check on initial load
      window.addEventListener("hashchange", handleHashChange); // Listen for hash changes
    }

    return () => {
      if (isBrowser) {
        window.removeEventListener("hashchange", handleHashChange); // Cleanup listener
      }
    };
  }, [title]);

  return (
    <li id={generateId(title)}>
      <StyledHeader
        isOpen={isOpen}
        onClick={toggleAccordion}
        aria-expanded={isOpen ? "true" : "false"}
      >
        <Typography component="span" variant="bodyBoldToLargeBold">
          {title}
        </Typography>
        <ChevronDown />
      </StyledHeader>
      <StyledBody isOpen={isOpen}>
        <Stack gap="fluidSmallToMedium">{content || components}</Stack>
      </StyledBody>
    </li>
  );
};

// Accordion Component
export const Accordion: FC<AccordionProps> = ({ items, ...rest }) => (
  <StyledAccordion gap="xSmall" as="ul" {...rest}>
    {items?.map((item, index) => (
      <AccordionItem key={index} {...item} />
    ))}
  </StyledAccordion>
);

export default Accordion;
